import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import c from 'classnames';

import { urls } from '@repo/app-constants';
import usePublishedGuides from 'services/general/guidesList';
import useCheckoutKit from 'hooks/useCheckoutKit';
import { useTinyAccountForHome } from 'hooks/useTinyAccountForHome';
import { usePortalConfigs } from 'hooks/usePortalConfigs';
import {
  EventPropertiesMap,
  ClickableEventNames,
} from 'contexts/analytics/types';
import { getImpersonateId } from 'store/user';
import Guide from 'types/Guide';

import {
  ButtonColors,
  ButtonSizes,
  ButtonVariants,
  Card,
  DesignButton,
  ImageFallback,
  TextLink,
} from 'components';
import { Typography, TextColor, Icon } from '@repo/ui';
import guidePlaceholder from 'assets/images/placeholders/book.svg';

import styles from './RecommendedForYou.module.scss';
import useFeature from 'services/features/useFeature';
import { kitResultsViewable } from '@repo/utils';

type RecommendedCardProps<E extends ClickableEventNames = 'Clicked Button'> = {
  title: string;
  label: React.ReactNode;
  imgStyles: string;
  cardColor: string;
  cardsLength?: number;
  analyticsEventName?: E;
  index?: number;
  clickable: EventPropertiesMap[E];
  noOverlay?: boolean;
};

const getRecommendedForYouCards = (
  RECOMMENDED_FOR_YOU_CARDS: {
    [key: string]: RecommendedCardProps<ClickableEventNames>;
  },
  getActiveStep: () => number,
  kitId: string | undefined,
  hasMembership: boolean | unknown,
  checkoutUrl: string,
  hideUpsells: boolean,
) => {
  const cards = [];

  const activeStep = getActiveStep();

  switch (activeStep) {
    case 0:
    case 1:
      cards.push(RECOMMENDED_FOR_YOU_CARDS.refer);
      cards.push(RECOMMENDED_FOR_YOU_CARDS.guides);
      cards.push(RECOMMENDED_FOR_YOU_CARDS.facebook);
      break;
    case 2:
    case 3:
    case 4:
    case -1: // All steps completed or last step hidden
      cards.push(RECOMMENDED_FOR_YOU_CARDS.actions);
      !hasMembership &&
        !hideUpsells &&
        cards.push(RECOMMENDED_FOR_YOU_CARDS.membership);
      !hideUpsells && cards.push(RECOMMENDED_FOR_YOU_CARDS.order);
      cards.push(RECOMMENDED_FOR_YOU_CARDS.guides);
      break;
    default:
      break;
  }

  return cards.map(card => {
    if (card?.clickable?.href.includes(':id')) {
      card.clickable.href = card.clickable.href.replace(':id', kitId ?? '');
    }
    if (card?.clickable?.href === ':checkoutUrl') {
      card.clickable.href = checkoutUrl;
    }
    return card;
  });
};

const RecommendedCard = <E extends ClickableEventNames = 'Clicked Button'>({
  title,
  label,
  imgStyles,
  cardColor,
  cardsLength = 0,
  index = 0,
  clickable,
  analyticsEventName,
  noOverlay = false,
}: RecommendedCardProps<E>) => {
  return (
    <Card<E>
      variant='wrapper'
      clickable={clickable}
      analyticsEventName={analyticsEventName}
      className={c(
        styles.card,
        cardsLength > 3 && index > 2 && 'hide-on-desktop',
        cardColor,
      )}
    >
      <div className={c(styles.cardBackground, imgStyles)}>
        <div className={c(styles.cardContent, !noOverlay && styles.overlay)}>
          <div className={styles.titleContainer}>
            <Typography variant='heading-m' className={styles.title}>
              {title}
            </Typography>
            <Typography variant='label' className={styles.label}>
              {label}
            </Typography>
          </div>
          <div className={styles.button}>
            <Icon name='chevronForward' size='s' />
          </div>
        </div>
      </div>
    </Card>
  );
};

export function RecommendedForYou({
  tinyAccountId,
}: {
  tinyAccountId: string | null;
}) {
  const { latestKit, hasMembership, getActiveStep } =
    useTinyAccountForHome(tinyAccountId);
  const { checkoutUrl, price } = useCheckoutKit({
    kitType: latestKit?.sampling_type,
    nonMemberUrl: urls.STORE,
  });
  const impersonateId = useSelector(getImpersonateId);
  const { config: portalConfig, isLoading: isPortalConfigLoading } =
    usePortalConfigs(latestKit);
  const { isFeatureEnabled: showABExperiment } = useFeature(
    'home_cards_a_b_experiment',
  );
  const { isFeatureEnabled: showGroupCalls } = useFeature(
    'consult_group_calls',
  );

  const RECOMMENDED_FOR_YOU_CARDS = useMemo(
    () => ({
      refer: {
        title: 'Give $40, Get $40',
        label: 'Refer a friend',
        imgStyles: styles.refer,
        cardColor: styles.colorRefer,
        clickable: {
          href: '/refer-a-friend',
          text: 'Refer a friend',
          isExternal: false,
        },
        analyticsEventName: 'Clicked Button' as const,
      },
      guides: {
        title: 'Explore Gut Guides',
        label: 'View All Guides',
        imgStyles: styles.guides,
        cardColor: styles.colorGuides,
        clickable: {
          href: '/guides',
          text: 'View all guides',
          isExternal: false,
        },
        analyticsEventName: 'Clicked Button' as const,
      },
      facebook: {
        title: 'Join Facebook Community',
        label: 'Go to Facebook',
        imgStyles: styles.facebook,
        cardColor: styles.colorFacebook,
        clickable: {
          href: urls.FACEBOOK_GROUP,
          text: 'Go to Facebook',
          isExternal: true,
        },
        analyticsEventName: 'Clicked Button' as const,
      },
      actions: {
        title: 'Your Action Plan is Ready',
        label: 'View Plan',
        imgStyles: styles.actions,
        cardColor: styles.colorActions,
        clickable: {
          href: '/action-plan/:id',
          text: 'View Plan',
          isExternal: false,
        },
        analyticsEventName: 'Clicked Button' as const,
      },
      membership: {
        title: showABExperiment
          ? 'Get Tiny+ Membership'
          : 'Unlock better health & save',
        label: showABExperiment
          ? 'If you plan to use more than 1 kit/year, Tiny+ is the best choice'
          : 'Explore Tiny+ membership',
        imgStyles: styles.membership,
        cardColor: styles.colorMembership,
        clickable: {
          href: urls.MEMBERSHIP,
          text: 'Explore Tiny+ membership',
          isExternal: true,
        },
        analyticsEventName: 'Click Home Tiny Plus Card' as const,
      },
      order: {
        title: 'Track your progress with your next kit',
        label: `Order kit for $${price}`,
        imgStyles: styles.order,
        cardColor: styles.colorOrder,
        clickable: {
          href: ':checkoutUrl',
          text: `Order kit for $${price}`,
          isExternal: true,
        },
        noOverlay: true,
        analyticsEventName: 'Clicked Button' as const,
      },
      consult: {
        title: showGroupCalls
          ? 'Schedule a review'
          : 'Schedule a coaching session',
        label: 'Included with your kit',
        imgStyles: styles.consult,
        cardColor: styles.colorConsult,
        clickable: {
          href: `/results/:id/consults/select-method`,
          text: 'Included with your kit',
          isExternal: false,
        },
        analyticsEventName: 'Clicked Button' as const,
      },
    }),
    [showABExperiment, price],
  );

  const cards = getRecommendedForYouCards(
    RECOMMENDED_FOR_YOU_CARDS,
    getActiveStep,
    latestKit?.id,
    hasMembership,
    checkoutUrl,
    !!portalConfig.hide_upsells || isPortalConfigLoading,
  );

  //If the surveys are completed and results are not ready we show guides instead of the other cards
  if (
    latestKit?.date_survey_completed &&
    !kitResultsViewable(latestKit, !!impersonateId)
  )
    return <GuidesForYou />;

  return (
    <div className={c(styles.container)}>
      <Typography variant='heading-m'>Recommended for you</Typography>
      <div className={styles.cardsContainer}>
        {cards.map((card, index) => (
          <RecommendedCard
            key={index}
            cardsLength={cards.length}
            {...card}
            index={index}
          />
        ))}
      </div>
    </div>
  );
}

const GuideCard = ({ guide }: { guide: Guide }) => {
  return (
    <Card
      variant='wrapper'
      clickable={
        !guide?.url
          ? undefined
          : {
              text: 'Guide: ' + guide.display_title,
              href: guide.url,
              variety: 'Card',
              isExternal: true,
            }
      }
    >
      <div className={styles.guideCard}>
        <div className={styles.content}>
          <div className={styles.imgContainer}>
            <ImageFallback src={guide.image_url} fallback={guidePlaceholder} />
          </div>
          <Typography variant='heading-m'>{guide.display_title}</Typography>
          <div className={styles.twoLinesOfText}>
            <Typography variant='body-s' responsive serif className='grey-text'>
              {guide.description}
            </Typography>
          </div>
        </div>
        <DesignButton
          label='Read more'
          variant={ButtonVariants.TEXT}
          size={ButtonSizes.M}
          color={ButtonColors.PURPLE}
        />
      </div>
    </Card>
  );
};

const GuidesForYou = () => {
  const { data: guides } = usePublishedGuides();
  const guidesToShow = useMemo(() => guides?.slice(0, 3), [guides]);

  if (!guidesToShow?.length) return <></>;
  return (
    <div className={styles.container}>
      <div className='flex align-items-center justify-content-between gap-1'>
        <Typography variant='heading-m'>Recent guides</Typography>
        <TextLink
          label='View all guides'
          iconNameRight='chevronForward'
          to='/guides'
          size='label'
          color={TextColor.PURPLE}
        />
      </div>
      <div className={styles.cardsContainer}>
        {guidesToShow.map(guide => (
          <GuideCard key={guide.id} guide={guide} />
        ))}
      </div>
    </div>
  );
};
